import React, { CSSProperties } from 'react';
import { Grid as MantineGrid } from '@mantine/core';

type Alignment = 'start' | 'center' | 'end' | 'stretch';
type Justification = 'start' | 'center' | 'end' | 'space-between' | 'space-around';
type ColSpan = number | 'auto' | 'content' | 'fill';

interface GridProps {
	alignItems?: Alignment;
	justifyItems?: Justification;
	numberOfColumns?: number;
	onClick?: React.MouseEventHandler<HTMLDivElement>;
	children: React.ReactNode;
	grow?: boolean;
}

const alignmentToMantineAlign: Record<Alignment, string> = {
	start: 'flex-start',
	end: 'flex-end',
	center: 'center',
	stretch: 'stretch',
};

const justificationToMantineJustify: Record<Justification, string> = {
	start: 'flex-start',
	end: 'flex-end',
	center: 'center',
	'space-between': 'space-between',
	'space-around': 'space-around',
};

const defaultAlignment: Alignment = 'stretch';
function convertAlignmentToMantineAlign(alignment: Alignment): string {
	return alignmentToMantineAlign[alignment];
}

const defaultJustification: Justification = 'start';
function convertJustifyToMantineJustify(justify: Justification): string {
	return justificationToMantineJustify[justify];
}

export const Grid: React.FC<React.PropsWithChildren<GridProps>> = ({
	alignItems = defaultAlignment,
	justifyItems = defaultJustification,
	numberOfColumns = 12,
	children,
	grow = false,
}) => (
	<MantineGrid
		{...(grow ? { grow } : {})}
		align={convertAlignmentToMantineAlign(alignItems)}
		justify={convertJustifyToMantineJustify(justifyItems)}
		columns={numberOfColumns}
	>
		{children}
	</MantineGrid>
);

interface GridColProps {
	alignItems?: Alignment;
	justifyItems?: Justification;
	span?: ColSpan;
	children: React.ReactNode;
}

export const GridCol: React.FC<React.PropsWithChildren<GridColProps>> = (props) => {
	const { alignItems = defaultAlignment, justifyItems = defaultJustification, span = 1, children } = props;

	let flexStyle: CSSProperties = {};
	if (span === 'fill') {
		flexStyle.flex = '1 1 100%';
	} else if (typeof span === 'number') {
		flexStyle.flexGrow = 1;
	} else {
		flexStyle.flex = '0 1 auto';
	}

	return (
		<MantineGrid.Col
			span={typeof span === 'number' ? span : undefined} // Make sure to not pass non-number values to Mantine's span prop
			style={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: convertJustifyToMantineJustify(justifyItems),
				alignItems: convertAlignmentToMantineAlign(alignItems),
				...flexStyle,
			}}
		>
			{children}
		</MantineGrid.Col>
	);
};
